import React from "react";
import { Check, Loader2, AlertCircle, Wallet, FileText, Search, ShieldCheck } from "lucide-react";

/**
 * Payment progress bar — 4 steps.
 * status:
 *   - "idle"     : awaiting hash input
 *   - "received" : hash entered (valid format)
 *   - "checking" : on-chain verification in progress
 *   - "ok"       : payment confirmed (order accepted)
 *   - "error"    : verification failed (sticks at "checking" with error tone)
 */
export default function PaymentProgress({ status = "idle", message = "" }) {
  const steps = [
    { id: "awaiting", label: "En attente du paiement",  icon: Wallet },
    { id: "received", label: "Hash reçu",               icon: FileText },
    { id: "checking", label: "Vérification blockchain", icon: Search },
    { id: "ok",       label: "Confirmé",                icon: ShieldCheck },
  ];

  // Map status → reached step index
  const reached = {
    idle: 0,
    received: 1,
    checking: 2,
    ok: 3,
    error: 2, // stuck on verification step
  }[status] ?? 0;

  const isError = status === "error";
  const isLoading = status === "checking";

  return (
    <div
      className="glass rounded-2xl p-5 sm:p-6"
      data-testid="payment-progress"
      data-status={status}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs uppercase tracking-[0.15em] text-zinc-500">
          Suivi du paiement
        </div>
        {isError && (
          <span className="text-[11px] text-red-300 inline-flex items-center gap-1">
            <AlertCircle className="h-3.5 w-3.5" /> Échec de la vérification
          </span>
        )}
      </div>

      <ol className="grid grid-cols-4 gap-2 sm:gap-3">
        {steps.map((s, idx) => {
          const done = idx < reached;
          const active = idx === reached && !isError;
          const errored = idx === reached && isError;
          const Icon = s.icon;
          return (
            <li
              key={s.id}
              className="flex flex-col items-center gap-2 text-center"
              data-testid={`pay-step-${s.id}`}
              data-state={done ? "done" : active ? "active" : errored ? "error" : "idle"}
            >
              <div className="relative w-full">
                {idx > 0 && (
                  <div
                    className={`absolute top-1/2 -translate-y-1/2 right-1/2 w-full h-0.5 ${
                      done ? "bg-cyan-400" : errored ? "bg-red-400" : "bg-white/10"
                    }`}
                    style={{ left: "-50%" }}
                  />
                )}
                <div
                  className={`relative mx-auto w-9 h-9 rounded-full flex items-center justify-center border transition-colors ${
                    done
                      ? "bg-cyan-400 border-cyan-400 text-[#05050A]"
                      : active
                        ? "bg-cyan-400/20 border-cyan-400 text-cyan-200"
                        : errored
                          ? "bg-red-400/20 border-red-400 text-red-200"
                          : "bg-white/[0.03] border-white/10 text-zinc-500"
                  }`}
                >
                  {done ? (
                    <Check className="h-4 w-4" />
                  ) : active && isLoading && idx === 2 ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : errored ? (
                    <AlertCircle className="h-4 w-4" />
                  ) : (
                    <Icon className="h-4 w-4" />
                  )}
                </div>
              </div>
              <div
                className={`text-[10px] sm:text-[11px] leading-tight ${
                  done || active
                    ? "text-zinc-200"
                    : errored
                      ? "text-red-300"
                      : "text-zinc-500"
                }`}
              >
                {s.label}
              </div>
            </li>
          );
        })}
      </ol>

      {message && (
        <div
          className={`mt-4 text-xs leading-relaxed ${
            isError ? "text-red-300" : "text-zinc-400"
          }`}
        >
          {message}
        </div>
      )}
    </div>
  );
}
